/**
 * The storage inventory disclosed by the Cookie Policy (src/app/cookies.tsx,
 * docs/legal/cookie-policy.md). Kept apart from the screen so the mirror test
 * can check it against both the document and the persisted stores in src/.
 */

import type { StorageRow } from '@/components/legal/LegalBlocks';

export const COOKIE_POLICY_UPDATED = 'October 19, 2026';

export const STRICTLY_NECESSARY: StorageRow[] = [
  {
    names: ['sb-<project>-auth-token'],
    where: 'Web: local storage. iOS: secure storage (Keychain)',
    purpose:
      'Keeps you signed in. On the web it is encrypted with a key held in your browser (see next row) where your browser supports it.',
    party: 'First party',
    duration: 'Until you sign out; renewed while you use the Service',
  },
  {
    names: ['inverio-secure'],
    where: 'Web: IndexedDB',
    purpose: 'Holds the encryption key for the sign-in token above. The key cannot be exported from your browser.',
    party: 'First party',
    duration: "Until you clear this site's data",
  },
  {
    names: ['pendingAuthRole'],
    where: 'Web: session storage',
    purpose: 'Remembers whether you are signing up as a founder or an investor while you are redirected to sign in.',
    party: 'First party',
    duration: 'Removed after sign-in, or when the tab closes',
  },
  {
    names: ['ExpoWebBrowserRedirectHandle', 'ExpoWebBrowser_OriginUrl_…', 'ExpoWebBrowser_RedirectUrl_…'],
    where: 'Web: local storage',
    purpose:
      'Completes a sign-in or account connection that runs in a pop-up (for example LinkedIn, Google Drive or Dropbox).',
    party: 'First party',
    duration: 'Removed when the sign-in completes',
  },
  {
    names: ['inverio-google-drive-tokens', 'inverio-dropbox-tokens'],
    where: 'iOS: secure storage',
    purpose: 'Access tokens for Google Drive or Dropbox, only if you connect one. They are never sent to our servers.',
    party: 'First party',
    duration: 'Until you disconnect or sign out',
  },
];

export const FUNCTIONAL: StorageRow[] = [
  {
    names: ['inverio-theme'],
    where: 'Web: local storage. iOS: app storage',
    purpose: 'Your light or dark theme.',
    party: 'First party',
    duration: 'Until you clear it; kept when you sign out',
  },
  {
    names: [
      'inverio-profile',
      'inverio-subscription',
      'inverio-usage',
      'inverio-dealflow',
      'inverio-dataroom',
      'inverio-score-history',
      'inverio-team',
      'inverio-thesis',
      'inverio-referrals',
      'inverio-google-drive',
      'inverio-dropbox',
    ],
    where: 'Web: local storage. iOS: app storage',
    purpose:
      'A cached copy of your own profile, plan, usage counts, deal flow, data room, score history, team, investment thesis, referrals and connected-storage status.',
    party: 'First party',
    duration: 'Emptied when you sign out',
  },
  {
    names: ['inverio_exec_summary_tab'],
    where: 'Web: local storage. iOS: app storage',
    purpose: 'Which tab of the executive summary you last opened.',
    party: 'First party',
    duration: 'Until you clear it',
  },
  {
    names: ['inverio_app_mode_override', 'inverio_dev_menu_unlocked'],
    where: 'Web: local storage. iOS: secure storage',
    purpose: 'Internal testing tools. Set only when a developer menu is used; not set during normal use.',
    party: 'First party',
    duration: 'Until cleared',
  },
];

export const ANALYTICS: StorageRow[] = [
  {
    names: ['@inverio/analytics_queue'],
    where: 'Web: local storage. iOS: app storage',
    purpose:
      'Holds usage events (screens viewed, features used, platform, app version and device information, tied to your account) until they are sent to our own database.',
    party: 'First party',
    duration:
      'Emptied as events are sent (about every 10 seconds), when you sign out, and when you switch Usage analytics off',
  },
];
